import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { isOfflineSessionValid, getOfflineSessionDaysRemaining } from '@/lib/offlineSession';

export interface OfflineFeature {
  key: string;
  label: string;
  available: boolean;
}

// CRITICAL: Check if current page is a print or family-accept route at MODULE LEVEL
// Never show offline overlays on these pages - this must be synchronous
const BYPASS_ROUTE_PATTERN = /\/(print\/?$|family\/accept)/;
const isBypassRouteAtLoad = typeof window !== 'undefined' && BYPASS_ROUTE_PATTERN.test(window.location.pathname);

const isBypassRoute = () => {
  if (typeof window !== 'undefined') {
    return BYPASS_ROUTE_PATTERN.test(window.location.pathname);
  }
  return false;
};

export const useOfflineMode = () => {
  const { t } = useTranslation();
  const { effectiveTier, isOfflineMode, offlineSessionDaysRemaining: contextDaysRemaining } = useSubscription();
  
  // CRITICAL: Use module-level check for bypass routes to avoid any flicker/delay
  const onBypassRoute = isBypassRouteAtLoad || isBypassRoute();
  
  // Use context's offline state as single source of truth, but bypass for special routes
  // For bypass routes, ALWAYS report as online to prevent any blocking UI
  const isOffline = onBypassRoute ? false : isOfflineMode;
  
  const [isPWA, setIsPWA] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [hasValidOfflineSession, setHasValidOfflineSession] = useState(false);
  const [sessionDaysRemaining, setSessionDaysRemaining] = useState(0);
  
  // CRITICAL: Track initialization state to prevent premature overlay display
  // For bypass routes, always consider initialized to prevent any blocking
  const [isInitialized, setIsInitialized] = useState(onBypassRoute);

  // Check if user has paid tier (pro or chef_master) - either from online or cached session
  const isPaidUser = effectiveTier === 'pro' || effectiveTier === 'chef_master';
  const canUseOfflineMode = isPaidUser && hasValidOfflineSession;

  // Check offline session validity - only once on mount and when offline state changes
  useEffect(() => {
    let isMounted = true;
    
    const checkSession = async () => {
      try {
        const valid = await isOfflineSessionValid();
        if (!isMounted) return;
        
        setHasValidOfflineSession(valid);
        
        if (valid) {
          const days = await getOfflineSessionDaysRemaining();
          if (isMounted) {
            setSessionDaysRemaining(days);
          }
        }
      } catch (error) {
        console.error('[useOfflineMode] Session check failed:', error);
        if (isMounted) {
          setHasValidOfflineSession(false);
        }
      } finally {
        if (isMounted) {
          setIsInitialized(true);
        }
      }
    };
    
    checkSession();
    
    return () => {
      isMounted = false;
    };
  }, [isOffline, effectiveTier]);

  useEffect(() => {
    // Check if running as PWA
    const checkPWA = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isIOSPWA = (window.navigator as any).standalone === true;
      setIsPWA(isStandalone || isIOSPWA);
    };
    checkPWA();

    // Listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = (e: MediaQueryListEvent) => {
      setIsPWA(e.matches);
    };
    mediaQuery.addEventListener('change', handleDisplayModeChange);

    return () => {
      mediaQuery.removeEventListener('change', handleDisplayModeChange);
    };
  }, []);

  // Reset dismissed state when coming back online
  useEffect(() => {
    if (!isOffline) {
      setDismissed(false);
    }
  }, [isOffline]);

  const unavailableFeatures: OfflineFeature[] = [
    { key: 'chaosKitchen', label: t('offline.features.chaosKitchen', 'Generate AI Recipes'), available: false },
    { key: 'mealPlan', label: t('offline.features.mealPlan', 'AI Meal Plans'), available: false },
    { key: 'recipeImport', label: t('offline.features.recipeImport', 'Import Recipes'), available: false },
    { key: 'imageGen', label: t('offline.features.imageGen', 'Image Generation'), available: false },
    { key: 'nutrition', label: t('offline.features.nutrition', 'Nutrition Calculation'), available: false },
    { key: 'voiceGuidance', label: t('offline.features.voiceGuidance', 'Voice Guidance'), available: false },
    { key: 'search', label: t('offline.features.search', 'Recipe Search'), available: false },
    { key: 'cloudSave', label: t('offline.features.cloudSave', 'Cloud Saving'), available: false },
    { key: 'comments', label: t('offline.features.comments', 'Comments & Ratings'), available: false },
  ];

  const availableFeatures: OfflineFeature[] = [
    { key: 'savedRecipes', label: t('offline.features.savedRecipes', 'View Saved Recipes'), available: true },
    { key: 'recipePacks', label: t('offline.features.recipePacks', 'Pre-cached Recipe Packs'), available: true },
    { key: 'cookMode', label: t('offline.features.cookMode', 'Cooking Mode'), available: true },
    { key: 'browseApp', label: t('offline.features.browseApp', 'Browse App UI'), available: true },
  ];

  const dismissBanner = useCallback(() => {
    setDismissed(true);
  }, []);

  // CRITICAL: Only show overlays/banners after initialization is complete
  // This prevents the brief flash of offline UI while session is being validated
  const showBanner = isInitialized && isOffline && !dismissed && canUseOfflineMode;
  const showUpgradeOverlay = isInitialized && isOffline && !canUseOfflineMode && !isPaidUser;

  return {
    isOffline,
    isPWA,
    isPaidUser,
    isLoggedIn: isPaidUser, // Simplified - paid users are logged in
    canUseOfflineMode,
    hasValidOfflineSession,
    sessionDaysRemaining: contextDaysRemaining || sessionDaysRemaining,
    showBanner,
    showUpgradeOverlay,
    unavailableFeatures,
    availableFeatures,
    dismissBanner,
    isInitialized,
  };
};
